import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { queuePostLoginWelcome } from './auth/PostLoginWelcome';
import { safeReturnPath } from './admin/adminUtils';
import { useSiteSettings } from './SiteSettingsContext';

function loginPath(searchParams, mode) {
  const params = new URLSearchParams(searchParams);
  if (mode === 'register') params.set('mode', 'register');
  else params.delete('mode');
  const q = params.toString();
  return q ? `/login?${q}` : '/login';
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { settings: site } = useSiteSettings();
  const auth = site.auth_copy || {};
  const benefits = Array.isArray(auth.benefits) && auth.benefits.length ? auth.benefits : [];
  const { login, register, isAuthed, isAdmin, ready } = useAuth();
  const [mode, setMode] = useState(searchParams.get('mode') === 'register' ? 'register' : 'signin');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const nextRaw = searchParams.get('next') || '';
  const checkoutNext = nextRaw.includes('/checkout');
  const isRegister = mode === 'register';

  useEffect(() => {
    setMode(searchParams.get('mode') === 'register' ? 'register' : 'signin');
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [mode]);

  useEffect(() => {
    if (!ready || !isAuthed) return;
    if (isAdmin) navigate(safeReturnPath(nextRaw, '/admin'), { replace: true });
    else navigate(safeReturnPath(nextRaw, '/account'), { replace: true });
  }, [ready, isAuthed, isAdmin, navigate, nextRaw]);

  const afterAuth = (res) => {
    queuePostLoginWelcome(res.username || displayName || username);
    if (res.role === 'admin') {
      navigate(safeReturnPath(nextRaw, '/admin'), { replace: true });
    } else {
      navigate(safeReturnPath(nextRaw, '/account'), { replace: true });
    }
  };

  const onSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await login(username, password, remember);
      if (!res.ok) {
        setError(res.error || 'Sign-in failed.');
        return;
      }
      afterAuth(res);
    } finally {
      setBusy(false);
    }
  };

  const onRegister = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await register({
        username,
        password,
        confirmPassword,
        displayName,
        email,
        phone,
        remember,
      });
      if (!res.ok) {
        setError(res.error || 'Registration failed.');
        return;
      }
      afterAuth(res);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="store-auth-page">
      <Helmet>
        <title>{isRegister ? 'Create account' : 'Sign in'} | Saloni Designs</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="container">
        <div className="store-auth-page__grid">
        <aside className="store-auth-aside">
          <div className="store-auth-aside__inner">
            <Link to="/" className="store-auth-aside__brand">
              <img src="/saloni-logo.png" alt="" className="store-auth-aside__logo" />
              <span>Saloni Designs</span>
            </Link>
            <h1 className="store-auth-aside__title">
              {checkoutNext
                ? isRegister
                  ? auth.register_checkout_aside_title || 'Create an account to checkout'
                  : auth.signin_checkout_aside_title || 'Sign in to complete your order'
                : isRegister
                  ? auth.register_aside_title || 'Join Saloni Designs'
                  : auth.signin_aside_title || 'Welcome back'}
            </h1>
            <p className="store-auth-aside__text">
              {isRegister
                ? auth.register_aside_text ||
                  'Register with a username and password. Your email and phone help us deliver orders and send updates.'
                : auth.signin_aside_text ||
                  'Sign in with your username and password to checkout, track orders, and manage your profile.'}
            </p>
            <ul className="store-auth-aside__list">
              {benefits.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="store-auth-main">
          <div className="store-auth-card">
            <div className="store-auth-card__mobile-brand d-lg-none text-center mb-3">
              <img src="/saloni-logo.png" alt="" width={48} height={48} className="rounded-circle mb-2" />
              <p className="small text-muted mb-0">Saloni Designs</p>
            </div>

            <h2 className="store-auth-card__title">
              {isRegister ? 'Create your account' : 'Sign in'}
            </h2>
            {checkoutNext && !isRegister && auth.signin_checkout_subtitle ? (
              <p className="store-auth-card__subtitle small text-muted mb-0">{auth.signin_checkout_subtitle}</p>
            ) : null}
            <ul className="store-auth-benefits d-lg-none list-unstyled small text-muted mt-2 mb-0">
              {benefits.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>

            {error ? (
              <div className="alert alert-danger py-2 small mt-3" role="alert">
                {error}
              </div>
            ) : null}

            {mode === 'signin' ? (
              <>
                <form onSubmit={onSignIn} noValidate className="auth-form mt-3">
                  <div className="checkout-field">
                    <label className="checkout-field__label" htmlFor="login-username">
                      Username or email
                    </label>
                    <input
                      id="login-username"
                      type="text"
                      className="form-control checkout-input"
                      autoComplete="username email"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      disabled={busy}
                      required
                    />
                  </div>
                  <div className="checkout-field">
                    <label className="checkout-field__label" htmlFor="login-password">
                      Password
                    </label>
                    <input
                      id="login-password"
                      type="password"
                      className="form-control checkout-input"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={busy}
                      required
                    />
                  </div>
                  <div className="form-check">
                    <input
                      id="login-remember"
                      className="form-check-input"
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      disabled={busy}
                    />
                    <label className="form-check-label small" htmlFor="login-remember">
                      Remember on this device
                    </label>
                  </div>
                  <button type="submit" className="btn btn-primary btn-lg rounded-pill w-100 store-cta" disabled={busy}>
                    {busy ? 'Signing in…' : 'Sign in'}
                  </button>
                </form>
                <p className="small text-center mt-3 mb-0">
                  <Link to="/forgot-password">Forgot password?</Link>
                </p>
                <div className="store-auth-guest-help alert alert-light border small text-start mt-4 mb-0" role="note">
                  <p className="fw-semibold text-body mb-2">Placed an order without signing up?</p>
                  <ul className="mb-2 ps-3">
                    <li>
                      <Link to="/track-order">Track your order</Link> with your order number and checkout email or
                      phone — no password needed.
                    </li>
                    <li>
                      We created an account from your checkout email. Use{' '}
                      <Link to="/forgot-password">Forgot password</Link> to set a password, then sign in with your
                      email.
                    </li>
                  </ul>
                </div>
                <p className="auth-mode-switch small text-center mt-4 mb-0">
                  {auth.signin_new_prompt || 'New to Saloni Designs?'}{' '}
                  <Link to={loginPath(searchParams, 'register')}>Create an account</Link>
                </p>
              </>
            ) : (
              <>
                <form onSubmit={onRegister} noValidate className="auth-form mt-3">
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="checkout-field">
                        <label className="checkout-field__label" htmlFor="reg-display-name">
                          Full name <span className="checkout-field__req">*</span>
                        </label>
                        <input
                          id="reg-display-name"
                          type="text"
                          className="form-control checkout-input"
                          autoComplete="name"
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          disabled={busy}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="checkout-field">
                        <label className="checkout-field__label" htmlFor="reg-email">
                          Email <span className="checkout-field__req">*</span>
                        </label>
                        <input
                          id="reg-email"
                          type="email"
                          className="form-control checkout-input"
                          autoComplete="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          disabled={busy}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="checkout-field">
                        <label className="checkout-field__label" htmlFor="reg-phone">
                          Phone
                        </label>
                        <input
                          id="reg-phone"
                          type="tel"
                          className="form-control checkout-input"
                          autoComplete="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          disabled={busy}
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="checkout-field">
                        <label className="checkout-field__label" htmlFor="reg-username">
                          Username <span className="checkout-field__req">*</span>
                        </label>
                        <input
                          id="reg-username"
                          type="text"
                          className="form-control checkout-input"
                          autoComplete="username"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          disabled={busy}
                          required
                        />
                        <p className="checkout-field__hint">3–32 characters; letters, numbers, hyphens.</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="checkout-field">
                        <label className="checkout-field__label" htmlFor="reg-password">
                          Password <span className="checkout-field__req">*</span>
                        </label>
                        <input
                          id="reg-password"
                          type="password"
                          className="form-control checkout-input"
                          autoComplete="new-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          disabled={busy}
                          minLength={8}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="checkout-field">
                        <label className="checkout-field__label" htmlFor="reg-confirm-password">
                          Confirm <span className="checkout-field__req">*</span>
                        </label>
                        <input
                          id="reg-confirm-password"
                          type="password"
                          className="form-control checkout-input"
                          autoComplete="new-password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          disabled={busy}
                          minLength={8}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  {auth.register_privacy_note ? (
                    <p className="auth-privacy-note small text-muted mt-2 mb-0">{auth.register_privacy_note}</p>
                  ) : null}
                  <div className="form-check">
                    <input
                      id="reg-remember"
                      className="form-check-input"
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      disabled={busy}
                    />
                    <label className="form-check-label small" htmlFor="reg-remember">
                      Remember on this device
                    </label>
                  </div>
                  <button type="submit" className="btn btn-primary btn-lg rounded-pill w-100 store-cta" disabled={busy}>
                    {busy ? 'Creating account…' : 'Create account'}
                  </button>
                </form>
                <p className="auth-mode-switch small text-center mt-4 mb-0">
                  Already have an account?{' '}
                  <Link to={loginPath(searchParams, 'signin')}>Sign in</Link>
                </p>
              </>
            )}

            <p className="store-auth-card__footer small text-muted text-center mt-4 mb-0">
              <Link to="/">← Back to shop</Link>
            </p>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
